import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Wallet, LayoutDashboard, CreditCard, Send, TrendingUp, History, User, LogOut, Menu, X, Shield, DollarSign, Bitcoin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { profileAPI } from '../services/api';
import { signOut } from 'firebase/auth';
import { auth } from '../services/firebase';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [currencyDisplay, setCurrencyDisplay] = useState('USDT');

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      setUserProfile(response.data);
      setCurrencyDisplay(response.data.currency_display || 'USDT');
      // Store in localStorage for other components to access
      localStorage.setItem('currency_display', response.data.currency_display || 'USDT');
      localStorage.setItem('user_profile', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  const toggleCurrencyDisplay = async () => {
    const displays = ['AED', 'USDT', 'CRYPTO'];
    const currentIndex = displays.indexOf(currencyDisplay);
    const newDisplay = displays[(currentIndex + 1) % displays.length];
    setCurrencyDisplay(newDisplay);
    localStorage.setItem('currency_display', newDisplay);
    
    // Update on server
    try {
      await profileAPI.updateProfile({ currency_display: newDisplay });
    } catch (error) {
      console.error('Failed to update currency preference');
    }
    
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('currencyDisplayChanged', { detail: newDisplay }));
  };

  const handleLogout = async () => {
    // Sign out of Firebase — this is the source of truth for auth state.
    // We intentionally skip Dynamic's handleLogOut() because it tears down
    // the SDK context before React can finish re-rendering, causing a crash
    // when DynamicWidget on the /auth page tries to use the destroyed context.
    // The Dynamic session is client-side only and resets on page reload.
    await signOut(auth).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currency_display');
    localStorage.removeItem('user_profile');
    // Full page reload so the Dynamic SDK re-initialises cleanly.
    // Client-side navigate('/auth') leaves the SDK in a stale state.
    window.location.href = '/auth';
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/wallet', icon: Wallet, label: 'Wallet' },
    { path: '/send', icon: Send, label: 'Send' },
    { path: '/convert', icon: TrendingUp, label: 'Convert' },
    { path: '/cards', icon: CreditCard, label: 'Cards' },
    { path: '/transactions', icon: History, label: 'History' },
    { path: '/kyc', icon: Shield, label: 'KYC' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const isActive = (path) => location.pathname === path;
  const displayName = userProfile?.nickname || userProfile?.full_name || 'User';

  return (
    <div className="min-h-screen bg-[#030014] relative">
      {/* Background Effects */}
      <div className="fixed inset-0 dark-mesh pointer-events-none" />
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-[#7000FF] opacity-10 blur-[150px] rounded-full pointer-events-none" />
      
      <div className="relative z-10">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
          <div className="flex flex-col flex-grow pt-8 pb-4 overflow-y-auto border-r border-white/10">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 px-6 mb-6">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-primary font-bold text-white">CRYPTOPAY</span>
            </Link>

            {/* User Profile Section */}
            <Link to="/profile" className="mx-4 mb-6 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all" data-testid="sidebar-profile">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                  {userProfile?.profile_photo ? (
                    <img 
                      src={userProfile.profile_photo} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate" data-testid="sidebar-nickname">{displayName}</p>
                  <p className="text-gray-400 text-xs truncate">{userProfile?.email}</p>
                </div>
              </div>
            </Link>

              {/* Currency Toggle */}
              <div className="mx-4 mb-6">
                <button
                  onClick={toggleCurrencyDisplay}
                  className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                  data-testid="currency-toggle-btn"
                >
                  <span className="text-gray-400 text-sm">Display Currency</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-mono ${currencyDisplay === 'AED' ? 'text-[#00F0FF]' : currencyDisplay === 'USDT' ? 'text-[#26A17B]' : 'text-[#FFD700]'}`}>
                      {currencyDisplay}
                    </span>
                    {currencyDisplay === 'AED' ? (
                      <DollarSign className="w-4 h-4 text-[#00F0FF]" />
                    ) : currencyDisplay === 'USDT' ? (
                      <DollarSign className="w-4 h-4 text-[#26A17B]" />
                    ) : (
                      <Bitcoin className="w-4 h-4 text-[#FFD700]" />
                    )}
                  </div>
                </button>
              </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive(item.path)
                        ? 'bg-[#7000FF] text-white neon-glow-primary'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-secondary font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout */}
            <div className="px-4 mt-auto">
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-secondary font-medium">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A051E]/95 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center justify-between px-4 py-3">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-primary font-bold text-white">CRYPTOPAY</span>
            </Link>
            
            <div className="flex items-center gap-3">
              {/* Mobile Profile Photo */}
              <Link to="/profile" className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center">
                {userProfile?.profile_photo ? (
                  <img 
                    src={userProfile.profile_photo} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </Link>
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="lg:hidden fixed inset-y-0 right-0 z-40 w-72 bg-[#0A051E] border-l border-white/10 pt-20 pb-4 overflow-y-auto"
            >
              {/* Mobile User Profile */}
              <Link 
                to="/profile" 
                onClick={() => setMobileMenuOpen(false)}
                className="mx-4 mb-4 p-3 bg-white/5 rounded-xl flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                  {userProfile?.profile_photo ? (
                    <img 
                      src={userProfile.profile_photo} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{displayName}</p>
                  <p className="text-gray-400 text-xs truncate">{userProfile?.email}</p>
                </div>
              </Link>

              {/* Mobile Currency Toggle */}
              <div className="mx-4 mb-4">
                <button
                  onClick={toggleCurrencyDisplay}
                  className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl"
                >
                  <span className="text-gray-400 text-sm">Display</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-mono ${currencyDisplay === 'AED' ? 'text-[#00F0FF]' : currencyDisplay === 'USDT' ? 'text-[#26A17B]' : 'text-[#FFD700]'}`}>
                      {currencyDisplay}
                    </span>
                    {currencyDisplay === 'AED' ? (
                      <DollarSign className="w-4 h-4 text-[#00F0FF]" />
                    ) : currencyDisplay === 'USDT' ? (
                      <DollarSign className="w-4 h-4 text-[#26A17B]" />
                    ) : (
                      <Bitcoin className="w-4 h-4 text-[#FFD700]" />
                    )}
                  </div>
                </button>
              </div>

              <nav className="px-4 space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                        isActive(item.path)
                          ? 'bg-[#7000FF] text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-secondary font-medium">{item.label}</span>
                    </Link>
                    );
                })}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 mt-4"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-secondary font-medium">Logout</span>
                </button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="lg:pl-72">
          <main className="px-6 md:px-12 py-8 lg:py-12 pt-24 lg:pt-12">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
